import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { ApiClient, RealtimeSocket, wsUrl } from '@despachador/api-client';
import type { SessionMe } from '@despachador/ui';

const BASE = import.meta.env.VITE_API_URL ?? '';
/** The panel's client: its own refresh cookie (`desp_rt_admin`) and session across tabs. */
export const api = new ApiClient(BASE, { app: 'admin' });
export const socket = new RealtimeSocket(wsUrl(BASE), api.session);

export type Me = SessionMe;

interface SessionCtx {
  me: Me;
  can: (p: string) => boolean;
  feature: (f: string) => boolean;
  reload: () => Promise<void>;
}

const Ctx = createContext<SessionCtx | null>(null);
export const useSession = () => useContext(Ctx)!;

/**
 * The signed-in account for the panel's screens. The session itself (start-up, refresh, sign-out)
 * belongs to `SessionGate` in @despachador/ui; this only exposes the account and the socket.
 */
export function SessionProvider({ me, reload, children }: { me: Me; reload: () => Promise<void>; children: ReactNode }) {
  useEffect(() => {
    socket.connect();
    return () => socket.close();
  }, []);
  const perms = new Set(me.permissions);
  return <Ctx.Provider value={{ me, can: (p) => perms.has(p), feature: (f) => !!me.features[f], reload }}>{children}</Ctx.Provider>;
}

/** Subscribes to realtime messages for the lifetime of a component. */
export function useRealtime(type: string, fn: (msg: any) => void, deps: unknown[] = []) {
  useEffect(() => socket.on(type, fn), deps); // eslint-disable-line react-hooks/exhaustive-deps
}
